// LugaresEventoConfig.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { FaSearch, FaPlus, FaChevronLeft } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { getLugaresPaginados, createLugar, updateLugar, toggleLugarStatus } from '../../../components/http/Actions/LugarEventoAction';
// Components
import LugarTable from './components/LugarTable';
import CreateLugarModal from './components/CreateLugarModal';
import EditLugarModal from './components/EditLugarModal';
import ToggleStatusModal from './components/ToggleStatusModal';
import Notification from '../../../components/Notification';
import Pagination from '../../../components/Pagination';
import SearchBar from '../../../components/SearchBar';

const LugaresEventoConfig = () => {
  // Estado para almacenar la lista de lugares
  const [lugares, setLugares] = useState([]);
  
  // Estado para búsqueda
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  
  // Estado para paginación
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 5,
    totalCount: 0,
    totalPages: 0,
    hasNext: false,
    hasPrevious: false
  });
  
  // Estados para modales
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showToggleModal, setShowToggleModal] = useState(false);
  const [lugarToEdit, setLugarToEdit] = useState(null);
  const [lugarToToggle, setLugarToToggle] = useState(null);
  
  // Estado para nuevo lugar
  const [newLugar, setNewLugar] = useState({
    nombre: '',
    direccion: '',
    ciudad: '',
    region: '',
    descripcion: '',
    imagen: ''
  });

  // token
  const token = localStorage.getItem("token");
  // Estado para errores y notificaciones
  const [error, setError] = useState('');
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  const [isLoading, setIsLoading] = useState(false);

  // Efecto para debounce en la búsqueda
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Función para mostrar notificación
  const showNotification = useCallback((message, type) => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: '' });
    }, 5000);
  }, []);

  // Función para cargar lugares con paginación
  const fetchLugares = useCallback(async (page = 1, pageSize = 5, search = '') => {
    setIsLoading(true);
    try {
      await getLugaresPaginados(
        (data) => {
          if (data && data.data) {
            setLugares(data.data.data);
            setPagination({
              currentPage: data.data.currentPage,
              pageSize: data.data.pageSize,
              totalCount: data.data.totalCount,
              totalPages: data.data.totalPages,
              hasNext: data.data.hasNext,
              hasPrevious: data.data.hasPrevious
            });
          } else {
            console.error("La respuesta de la API no tiene el formato esperado:", data);
            setLugares([]);
          }
        },
        setError,
        page,
        pageSize,
        search,
        token
      );
    } catch (error) {
      console.error("Error fetching lugares:", error);
      showNotification("Error al cargar los lugares", "error");
    } finally {
      setIsLoading(false);
    }
  }, [showNotification, token]);

  // Función para cambiar de página
  const handlePageChange = (newPage) => {
    setPagination(prev => ({
      ...prev,
      currentPage: newPage
    }));
  };
  
  // Función para buscar
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setPagination(prev => ({
      ...prev,
      currentPage: 1
    }));
  };

  // Cargar lugares cuando cambia la página o el término de búsqueda
  useEffect(() => {
    fetchLugares(pagination.currentPage, pagination.pageSize, debouncedSearchTerm);
  }, [pagination.currentPage, debouncedSearchTerm, fetchLugares, pagination.pageSize]);

  // Función para abrir modal de edición
  const openEditModal = (lugar) => {
    setLugarToEdit({
      ...lugar
    });
    setShowEditModal(true);
  };

  // Función para abrir modal de creación
  const openCreateModal = () => {
    setNewLugar({
      nombre: '',
      direccion: '',
      ciudad: '',
      region: '',
      descripcion: '',
      imagen: ''
    });
    setShowCreateModal(true);
  };

  // Función para editar lugar
  const handleEditSubmit = async (e, editedLugar) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      // Preparar datos para el DTO de actualización
      const updateData = {
        nombre: editedLugar.nombre,
        direccion: editedLugar.direccion,
        ciudad: editedLugar.ciudad,
        region: editedLugar.region,
        descripcion: editedLugar.descripcion,
        imagen: editedLugar.imagen,
        habilitado: editedLugar.habilitado
      };

      await updateLugar(editedLugar.lugarId, updateData, setError, token);
      fetchLugares(pagination.currentPage, pagination.pageSize, debouncedSearchTerm);
      showNotification(`Lugar "${editedLugar.nombre}" actualizado con éxito`, "success");
      setShowEditModal(false);
    } catch (error) {
      console.error("Error updating lugar:", error);
      showNotification("Error al actualizar el lugar", "error");
    } finally {
      setIsLoading(false);
    }
  };

  // Función para crear lugar
  const handleCreateSubmit = async (e, newLugarData) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      await createLugar(newLugarData, setError, token);
      fetchLugares(pagination.currentPage, pagination.pageSize, debouncedSearchTerm);
      showNotification(`Lugar "${newLugarData.nombre}" creado con éxito`, "success");
      setShowCreateModal(false);
    } catch (error) {
      console.error("Error creating lugar:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Función para eliminar lugar
  /*
  const handleDeleteLugar = async (lugarId, nombre) => {
    if (window.confirm(`¿Estás seguro de eliminar el lugar "${nombre}"?`)) {
      setIsLoading(true);
      
      try {
        await deleteLugar(lugarId, setError, token);
        fetchLugares(pagination.currentPage, pagination.pageSize, debouncedSearchTerm);
        showNotification(`Lugar "${nombre}" eliminado con éxito`, "success");
      } catch (error) {
        console.error("Error deleting lugar:", error);
      } finally {
        setIsLoading(false);
      }
    }
  };*/

  // Función para abrir el modal de cambio de estado
  const handleToggleStatusClick = (lugar) => {
    setLugarToToggle(lugar);
    setShowToggleModal(true);
  };

  // Función para cambiar estado del lugar (habilitar/deshabilitar)
  const handleToggleStatus = async (lugar) => {
    setIsLoading(true);
    
    try {
      await toggleLugarStatus(lugar.lugarId, setError, token);
      fetchLugares(pagination.currentPage, pagination.pageSize, debouncedSearchTerm);
      showNotification(
        `Lugar "${lugar.nombre}" ${lugar.habilitado ? "deshabilitado" : "habilitado"} con éxito`, 
        "success"
      );
      setShowToggleModal(false);
    } catch (error) {
      console.error(`Error al cambiar estado del lugar:`, error);
      showNotification("Error al cambiar el estado del lugar", "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-900">
      {/* Header */}
      <header className="bg-zinc-800 shadow-lg">
        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8 flex justify-between items-center">
          <div className="flex items-center">
            <Link to="/admin" className="mr-4">
              <FaChevronLeft className="text-white hover:text-red-500 transition-colors" />
            </Link>
            <h1 className="text-3xl font-bold text-white">Gestor de Lugares de Evento</h1>
          </div>
          <button 
            onClick={openCreateModal}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center"
          >
            <FaPlus className="mr-2" />
            Nuevo Lugar
          </button>
        </div>
      </header>

      {/* Notification */}
      {notification.show && (
        <Notification message={notification.message} type={notification.type} />
      )}

      {/* Main content */}
      <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Search */}
        <SearchBar 
          searchTerm={searchTerm}
          handleSearch={handleSearch}
          placeholder="Buscar por nombre, ciudad o región..."
        />

        {/* Loading state */}
        {isLoading && (
          <div className="flex justify-center my-12">
            <div className="w-12 h-12 border-4 border-t-4 border-gray-200 border-t-red-600 rounded-full animate-spin"></div>
          </div>
        )}

        {/* Error state */}
        {error && !isLoading && (
          <div className="bg-red-500/20 border border-red-500 text-red-100 px-6 py-4 rounded-lg mb-6">
            <p>{error}</p>
          </div>
        )}

        {/* Lugares table */}
        {!isLoading && !error && (
          <LugarTable 
            lugares={lugares} 
            onEdit={openEditModal} 
            onToggleStatus={handleToggleStatusClick} 
          />
        )}
        
        {/* Paginación */}
        {!isLoading && !error && pagination.totalCount > 0 && (
          <Pagination 
            pagination={pagination} 
            handlePageChange={handlePageChange} 
          />
        )}
      </main>

      {/* Edit modal */}
      {showEditModal && lugarToEdit && (
        <EditLugarModal 
          lugarToEdit={lugarToEdit}
          setLugarToEdit={setLugarToEdit}
          handleEditSubmit={handleEditSubmit}
          isLoading={isLoading}
          onClose={() => setShowEditModal(false)}
        />
      )}

      {/* Create modal */}
      {showCreateModal && (
        <CreateLugarModal 
          newLugar={newLugar}
          setNewLugar={setNewLugar}
          handleCreateSubmit={handleCreateSubmit}
          isLoading={isLoading}
          onClose={() => setShowCreateModal(false)}
        />
      )}
      
      {/* Toggle Status modal */}
      {showToggleModal && lugarToToggle && (
        <ToggleStatusModal 
          lugar={lugarToToggle}
          onConfirm={handleToggleStatus}
          onClose={() => setShowToggleModal(false)}
          isLoading={isLoading}
        />
      )}
    </div>
  );
};

export default LugaresEventoConfig;